import React, { useState, useEffect } from 'react';
import { FileText, Clock, CheckCircle, Eye, Send } from 'lucide-react';

const FillForms = () => {
  const [assignedForms, setAssignedForms] = useState([
    { 
      id: 1, 
      name: 'Test Form Alpha', 
      description: 'Test description for form alpha',
      assignedDate: '2024-01-15', 
      dueDate: '2024-01-25',
      status: 'Pending',
      fields: [
        { id: 'name', label: 'Name', type: 'text', required: true },
        { id: 'email', label: 'Email', type: 'email', required: true },
        { id: 'district', label: 'District', type: 'dropdown', required: true, options: ['Chennai', 'Coimbatore', 'Madurai'] },
        { id: 'role', label: 'Role', type: 'dropdown', required: false, options: ['Section Officer', 'Police Inspector', 'DADWO Officer', 'Warden'] },
        { id: 'comments', label: 'Comments', type: 'textarea', required: false },
      ]
    },
    { 
      id: 2, 
      name: 'Test Form Beta', 
      description: 'Test description for form beta',
      assignedDate: '2024-01-14', 
      dueDate: '2024-01-24',
      status: 'Completed',
      fields: [
        { id: 'applicant_name', label: 'Applicant Name', type: 'text', required: true },
        { id: 'phone', label: 'Phone Number', type: 'number', required: true },
        { id: 'date', label: 'Application Date', type: 'date', required: true },
      ]
    },
    { 
      id: 3, 
      name: 'Test Form Gamma', 
      description: 'Test description for form gamma',
      assignedDate: '2024-01-13', 
      dueDate: '2024-01-23',
      status: 'Pending',
      fields: [
        { id: 'project_name', label: 'Project Name', type: 'text', required: true },
        { id: 'budget', label: 'Budget', type: 'number', required: true },
        { id: 'status', label: 'Project Status', type: 'dropdown', required: true, options: ['Planning', 'In Progress', 'Completed'] },
        { id: 'description', label: 'Description', type: 'textarea', required: true },
      ]
    },
  ]);

  const [showFillModal, setShowFillModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState({});

  const handleFillForm = (form) => {
    setSelectedForm(form);
    // Initialize form data with empty values
    const initialData = {};
    form.fields.forEach(field => {
      initialData[field.id] = '';
    });
    setFormData(initialData);
    setShowFillModal(true);
  };

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSubmitForm = () => {
    // Validate required fields
    const requiredFields = selectedForm.fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !formData[f.id]);

    if (missingFields.length > 0) {
      alert('Please fill all required fields');
      return;
    }

    // Submit form
    setAssignedForms(assignedForms.map(form => 
      form.id === selectedForm.id ? { ...form, status: 'Completed' } : form
    ));
    
    setShowFillModal(false);
    alert('Form submitted successfully!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'Completed':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <input
            type={field.type}
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
            required={field.required}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
            required={field.required}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            rows="4"
            className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
            required={field.required}
          />
        );
      
      case 'dropdown':
        return (
          <select
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Fill Forms</h1>
        <p className="text-sm text-gray-600 mt-1">Complete forms assigned to you</p>
      </div>

      {/* Forms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {assignedForms.map((form) => (
          <div
            key={form.id}
            className="bg-white border border-gray-200 p-6 hover:shadow-lg transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <span className={`px-3 py-1 text-xs font-semibold ${getStatusColor(form.status)}`}>
                {form.status}
              </span>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{form.name}</h3>
            <p className="text-sm text-gray-600 mb-4">{form.description}</p>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>Assigned: {form.assignedDate}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>Due: {form.dueDate}</span>
              </div>
            </div>

            {form.status === 'Pending' ? (
              <button
                onClick={() => handleFillForm(form)}
                className="w-full px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                Fill Form
              </button>
            ) : (
              <button
                className="w-full px-4 py-2 bg-green-50 text-green-700 font-medium border border-green-200 flex items-center justify-center gap-2"
                disabled
              >
                <CheckCircle className="w-4 h-4" />
                Completed
              </button>
            )}
          </div>
        ))}
      </div>

      {assignedForms.length === 0 && (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No forms assigned to you yet</p>
        </div>
      )}

      {/* Fill Form Modal */}
      {showFillModal && selectedForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">{selectedForm.name}</h2>
              <p className="text-sm text-gray-600 mt-1">{selectedForm.description}</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <form className="space-y-6">
                {selectedForm.fields.map((field) => (
                  <div key={field.id}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-600 ml-1">*</span>}
                    </label>
                    {renderField(field)}
                  </div>
                ))}
              </form>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowFillModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitForm}
                className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Submit Form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FillForms;